import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronRight,
  Settings2,
  Bell,
  BellOff,
  Activity,
  Gauge,
  Navigation,
  Zap,
  Thermometer,
  Waves,
  ArrowLeft,
  RefreshCw,
  Check,
  X,
  AlertTriangle,
  Info,
  AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ícones por categoria
const categoryIcons = {
  propulsion: Gauge,
  navigation: Navigation,
  electrical: Zap,
  environment: Waves,
  tanks: Waves,
  steering: Navigation,
  other: Activity,
};

// Cores por categoria
const categoryColors = {
  propulsion: 'text-orange-500',
  navigation: 'text-blue-500',
  electrical: 'text-yellow-500',
  environment: 'text-green-500',
  tanks: 'text-cyan-500',
  steering: 'text-purple-500',
  other: 'text-muted-foreground',
};

// Cores de severidade
const severityColors = {
  info: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
  warning: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
  critical: 'bg-red-500/20 text-red-500 border-red-500/30',
};

const severityIcons = {
  info: Info,
  warning: AlertTriangle,
  critical: AlertCircle,
};

const defaultMotorParameters = {
  rpm: { label: 'RPM' },
  temperature: { label: 'Temperatura' },
  oilPressure: { label: 'Pressão de Óleo' },
  oilTemperature: { label: 'Temperatura do Óleo' },
  boostPressure: { label: 'Pressão Turbo' },
  engineLoad: { label: 'Carga' },
  runTime: { label: 'Horímetro' },
  fuelRate: { label: 'Consumo' },
  transmissionGear: { label: 'Marcha' },
  transmissionOilTemperature: { label: 'Temp. Óleo Transmissão' },
  transmissionOilPressure: { label: 'Pressão Óleo Transmissão' },
};

const sourceStatusLabels = {
  live: 'Recebendo',
  static: 'Estático',
  stale: 'Sem atualização',
};

const sourceStatusClasses = {
  live: 'bg-green-500/10 text-green-500 border-green-500/30',
  static: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30',
  stale: 'bg-muted text-muted-foreground border-border',
};

const formatPreviewValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return Number.isFinite(value) ? value.toFixed(2) : String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatSourceInstance = (group) => (
  group?.source_instance_label || `Instância ${group?.raw_instance || '—'}`
);

// Componente de Source Item
const SourceItem = ({ item, onConfigure, onToggleAlert }) => {
  const { source, config, display } = item;
  const isConfigured = config.status === 'configured';
  const hasAlert = config.alert_enabled;
  
  return (
    <div 
      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors border border-border"
      data-testid={`source-item-${source.path}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {source.is_active ? (
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" title="Ativo" />
          ) : (
            <span className="w-2 h-2 rounded-full bg-muted-foreground" title="Inativo" />
          )}
          <span className="font-medium text-sm truncate">
            {config.display_name || source.path}
          </span>
          {isConfigured && (
            <Badge variant="outline" className="text-xs bg-green-500/10 text-green-500 border-green-500/30">
              Configurado
            </Badge>
          )}
          {hasAlert && (
            <Bell className="w-3 h-3 text-yellow-500" />
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-1 truncate">
          {source.path}
        </div>
      </div>
      
      <div className="flex items-center gap-4 ml-4">
        {/* Valor atual */}
        <div className="text-right min-w-[80px]">
          <div className={`text-lg font-mono font-semibold ${
            display.current_zone?.severity === 'critical' ? 'text-red-500' :
            display.current_zone?.severity === 'warning' ? 'text-yellow-500' :
            'text-foreground'
          }`}>
            {display.formatted_value}
          </div>
          <div className="text-xs text-muted-foreground">
            {display.unit}
          </div>
        </div>
        
        {/* Ações */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onConfigure(item)}
            title="Configurar"
            data-testid={`configure-btn-${source.path}`}
          >
            <Settings2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${hasAlert ? 'text-yellow-500' : ''}`}
            onClick={() => onToggleAlert(item)}
            title={hasAlert ? 'Desabilitar alertas' : 'Habilitar alertas'}
            data-testid={`alert-btn-${source.path}`}
          >
            {hasAlert ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Modal de Configuração
const ConfigModal = ({ isOpen, onClose, item, availableUnits, onSave }) => {
  const [formData, setFormData] = useState({
    display_name: '',
    display_unit: '',
    decimal_places: 2,
    min_value: null,
    max_value: null,
  });
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (item) {
      setFormData({
        display_name: item.config.display_name || item.source.path,
        display_unit: item.config.display_unit || '',
        decimal_places: item.config.decimal_places || 2,
        min_value: item.config.min_value,
        max_value: item.config.max_value,
      });
    }
  }, [item]);
  
  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(item.config.id, formData);
      onClose();
    } finally {
      setLoading(false);
    }
  };
  
  if (!item) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-card border-border" data-testid="config-modal">
        <DialogHeader>
          <DialogTitle>Configurar Source</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {item.source.path}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="display_name">Nome de Exibição</Label>
            <Input
              id="display_name"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              placeholder="Ex: RPM Motor BB"
              data-testid="input-display-name"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="display_unit">Unidade</Label>
              <Select
                value={formData.display_unit}
                onValueChange={(value) => setFormData({ ...formData, display_unit: value })}
              >
                <SelectTrigger id="display_unit" data-testid="select-unit">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {availableUnits.map((u) => (
                    <SelectItem key={u.unit} value={u.unit}>
                      {u.unit} {u.is_default && '(padrão)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="decimal_places">Casas Decimais</Label>
              <Input
                id="decimal_places"
                type="number"
                min="0"
                max="6"
                value={formData.decimal_places}
                onChange={(e) => setFormData({ ...formData, decimal_places: parseInt(e.target.value) || 0 })}
                data-testid="input-decimals"
              />
            </div>
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="min_value">Valor Mínimo</Label>
              <Input
                id="min_value"
                type="number"
                value={formData.min_value ?? ''}
                onChange={(e) => setFormData({ ...formData, min_value: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="Auto"
                data-testid="input-min"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="max_value">Valor Máximo</Label>
              <Input
                id="max_value"
                type="number"
                value={formData.max_value ?? ''}
                onChange={(e) => setFormData({ ...formData, max_value: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="Auto"
                data-testid="input-max"
              />
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={loading} data-testid="save-config-btn">
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Modal de Alertas
const AlertModal = ({ isOpen, onClose, item, onSave }) => {
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (item) {
      setAlertEnabled(item.config.alert_enabled || false);
      setZones(item.config.alert_zones || []);
    }
  }, [item]);
  
  const addZone = () => {
    setZones([
      ...zones,
      {
        zone_name: `Zona ${zones.length + 1}`,
        lower_bound: 0,
        upper_bound: 100,
        severity: 'info',
      }
    ]);
  };
  
  const removeZone = (index) => {
    setZones(zones.filter((_, i) => i !== index));
  };
  
  const updateZone = (index, field, value) => {
    const newZones = [...zones];
    newZones[index] = { ...newZones[index], [field]: value };
    setZones(newZones);
  };
  
  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(item.config.id, alertEnabled, zones);
      onClose();
    } finally {
      setLoading(false);
    }
  };
  
  if (!item) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] bg-card border-border max-h-[80vh]" data-testid="alert-modal">
        <DialogHeader>
          <DialogTitle>Configurar Alertas</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {item.config.display_name || item.source.path}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[50vh]">
          <div className="space-y-4 py-4 pr-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="alert_enabled" className="text-base">Alertas Habilitados</Label>
                <p className="text-sm text-muted-foreground">
                  Ativar monitoramento de zonas de alerta
                </p>
              </div>
              <Switch
                id="alert_enabled"
                checked={alertEnabled}
                onCheckedChange={setAlertEnabled}
                data-testid="alert-toggle"
              />
            </div>
            
            {alertEnabled && (
              <>
                <Separator />
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Zonas de Alerta</Label>
                    <Button variant="outline" size="sm" onClick={addZone} data-testid="add-zone-btn">
                      Adicionar Zona
                    </Button>
                  </div>
                  
                  {zones.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nenhuma zona configurada. Adicione zonas para definir faixas de operação.
                    </p>
                  )}
                  
                  {zones.map((zone, index) => {
                    const SeverityIcon = severityIcons[zone.severity];
                    return (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border ${severityColors[zone.severity]}`}
                        data-testid={`zone-${index}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <SeverityIcon className="w-4 h-4" />
                            <Input
                              value={zone.zone_name}
                              onChange={(e) => updateZone(index, 'zone_name', e.target.value)}
                              className="h-7 w-32 bg-transparent border-none p-0 text-sm font-medium"
                              placeholder="Nome da zona"
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-500 hover:text-red-300"
                            onClick={() => removeZone(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">Mínimo</Label>
                            <Input
                              type="number"
                              value={zone.lower_bound}
                              onChange={(e) => updateZone(index, 'lower_bound', parseFloat(e.target.value) || 0)}
                              className="h-8 bg-muted/50"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Máximo</Label>
                            <Input
                              type="number"
                              value={zone.upper_bound}
                              onChange={(e) => updateZone(index, 'upper_bound', parseFloat(e.target.value) || 0)}
                              className="h-8 bg-muted/50"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Severidade</Label>
                            <Select
                              value={zone.severity}
                              onValueChange={(value) => updateZone(index, 'severity', value)}
                            >
                              <SelectTrigger className="h-8 bg-muted/50">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="info">Info</SelectItem>
                                <SelectItem value="warning">Alerta</SelectItem>
                                <SelectItem value="critical">Crítico</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </ScrollArea>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={loading} data-testid="save-alerts-btn">
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SourceGroupWizard = ({
  open,
  onClose,
  group,
  parameterDefinitions,
  dashboards,
  onConfigure,
  onIgnore,
}) => {
  const [displayLabel, setDisplayLabel] = useState('');
  const [logicalInstanceId, setLogicalInstanceId] = useState('');
  const [selectedParameters, setSelectedParameters] = useState(['rpm', 'temperature', 'oilPressure', 'runTime']);
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (group) {
      const candidates = group.candidates?.length ? group.candidates : [group];
      const initialGroup = candidates[0];
      setSelectedGroupId(initialGroup?.id || null);
      setDisplayLabel(initialGroup?.display_label || '');
      setLogicalInstanceId(initialGroup?.logical_instance_id || '');
      setSelectedParameters(initialGroup?.selected_parameters?.length ? initialGroup.selected_parameters : ['rpm', 'temperature', 'oilPressure', 'runTime']);
      setSelectedDashboards(initialGroup?.configured_dashboard_ids || []);
    }
  }, [group]);

  if (!group) return null;

  const candidates = group.candidates?.length ? group.candidates : [group];
  const selectedGroup = candidates.find(candidate => candidate.id === selectedGroupId) || candidates[0];
  const definitions = parameterDefinitions || defaultMotorParameters;
  const toggleParameter = (key) => {
    setSelectedParameters(prev => (
      prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]
    ));
  };
  const toggleDashboard = (id) => {
    setSelectedDashboards(prev => (
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    ));
  };

  const handleSave = async () => {
    if (!selectedGroup?.id) return;
    setSaving(true);
    try {
      await onConfigure(selectedGroup.id, {
        display_label: displayLabel || group.source_set_label || selectedGroup.source_label,
        logical_instance_id: logicalInstanceId || null,
        parameters: selectedParameters,
        dashboard_ids: selectedDashboards,
        parameter_configs: {},
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] bg-card border-border max-h-[88vh]" data-testid="source-group-wizard">
        <DialogHeader>
          <DialogTitle>Configurar fonte NMEA detectada</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {group.source_set_label || group.source_label} • {candidates.length} instância(s) candidata(s)
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[64vh] pr-4">
          <div className="space-y-5 py-2">
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground">Fonte física</div>
                  <div className="font-mono">{group.source_set_label || selectedGroup?.source_label}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Instâncias candidatas</div>
                  <div>{candidates.length}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">PGNs observados</div>
                  <div>{group.observed_pgns?.length ? group.observed_pgns.join(', ') : selectedGroup?.observed_pgns?.join(', ') || '—'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Última leitura</div>
                  <div>{group.last_seen ? new Date(group.last_seen).toLocaleString() : selectedGroup?.last_seen ? new Date(selectedGroup.last_seen).toLocaleString() : '—'}</div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Label>Instâncias detectadas</Label>
              <div className="space-y-2">
                {candidates.map((candidate) => {
                  const selected = selectedGroup?.id === candidate.id;
                  const previewPaths = candidate.preview_paths || [];
                  const status = candidate.activity_status || 'stale';
                  return (
                    <button
                      type="button"
                      key={candidate.id}
                      onClick={() => setSelectedGroupId(candidate.id)}
                      className={`w-full rounded-md border p-3 text-left transition-colors ${
                        selected ? 'border-primary bg-primary/10' : 'border-border bg-card/40 hover:bg-muted/60'
                      }`}
                      data-testid={`source-candidate-${candidate.id}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{formatSourceInstance(candidate)}</span>
                            <Badge variant="outline" className={sourceStatusClasses[status] || sourceStatusClasses.stale}>
                              {sourceStatusLabels[status] || status}
                            </Badge>
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {candidate.source_label} • {candidate.observed_paths?.length || 0} path(s)
                            {candidate.observed_pgns?.length ? ` • PGNs ${candidate.observed_pgns.join(', ')}` : ''}
                          </div>
                        </div>
                        {candidate.last_seen && (
                          <div className="text-xs text-muted-foreground">
                            {new Date(candidate.last_seen).toLocaleString()}
                          </div>
                        )}
                      </div>
                      <div className="mt-3 grid gap-2 md:grid-cols-2">
                        {previewPaths.length ? previewPaths.slice(0, 6).map((preview) => (
                          <div key={preview.path} className="rounded border border-border bg-muted/40 p-2">
                            <div className="flex items-center justify-between gap-2 text-xs">
                              <span className="truncate text-muted-foreground">{preview.path}</span>
                              <span className="font-mono text-foreground">{formatPreviewValue(preview.last_value)}</span>
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                              {preview.pgn && <span>PGN {preview.pgn}</span>}
                              <span>{preview.sample_count_recent || 0} amostra(s)</span>
                              <span>{preview.change_count_recent || 0} variação(ões)</span>
                            </div>
                          </div>
                        )) : (
                          <div className="rounded border border-border bg-muted/40 p-2 text-xs text-muted-foreground">
                            Sem preview de valores recente para esta instância.
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="motor-label">Nome do motor/dispositivo</Label>
                <Input
                  id="motor-label"
                  value={displayLabel}
                  onChange={(e) => setDisplayLabel(e.target.value)}
                  placeholder="Ex: Motor da Draga"
                  data-testid="source-group-display-label"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="logical-id">ID lógico opcional</Label>
                <Input
                  id="logical-id"
                  value={logicalInstanceId}
                  onChange={(e) => setLogicalInstanceId(e.target.value)}
                  placeholder="Automático: engineN"
                  data-testid="source-group-logical-id"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Parâmetros monitorados</Label>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(definitions).map(([key, definition]) => (
                  <label key={key} className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                    <Checkbox
                      checked={selectedParameters.includes(key)}
                      onCheckedChange={() => toggleParameter(key)}
                    />
                    <span>{definition.label || key}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Adicionar cards aos dashboards</Label>
              {dashboards.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhum dashboard disponível para inclusão automática. A fonte será configurada e poderá ser adicionada a dashboards depois.
                </p>
              ) : (
                <div className="space-y-2">
                  {dashboards.map((dashboard) => (
                    <label key={dashboard.id} className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                      <Checkbox
                        checked={selectedDashboards.includes(dashboard.id)}
                        onCheckedChange={() => toggleDashboard(dashboard.id)}
                      />
                      <span>{dashboard.name}</span>
                      {dashboard.is_default && <Badge variant="secondary">Padrão</Badge>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => selectedGroup?.id && onIgnore(selectedGroup.id)} disabled={saving || !selectedGroup?.id}>
            Ignorar instância
          </Button>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || !selectedParameters.length} data-testid="source-group-save-btn">
            {saving ? 'Salvando...' : 'Configurar instância selecionada'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Modal de renomeação de motor (designação)
const EngineLabelModal = ({ isOpen, onClose, motor, onSave }) => {
  const [label, setLabel] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (motor) setLabel(motor.label || motor.id || '');
  }, [motor]);

  const handleSave = async () => {
    const trimmed = (label || '').trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      await onSave(motor.id, trimmed);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!motor) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="engine-label-modal">
        <DialogHeader>
          <DialogTitle>Renomear motor</DialogTitle>
          <DialogDescription>
            Designe um nome para <span className="font-mono">{motor.id}</span>. O nome é
            aplicado aos parâmetros deste motor (RPM, Temperatura, etc.) e ao histórico.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="engine-label">Nome do motor</Label>
          <Input
            id="engine-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ex: DRAGA"
            data-testid="engine-label-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button onClick={handleSave} disabled={loading || !label.trim()} data-testid="engine-label-save">
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Página Principal
const SourceRegistryPage = () => {
  const { t } = useTranslation();
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [configModal, setConfigModal] = useState({ open: false, item: null });
  const [alertModal, setAlertModal] = useState({ open: false, item: null });
  const [availableUnits, setAvailableUnits] = useState([]);
  const [sourceSets, setSourceSets] = useState([]);
  const [parameterDefinitions, setParameterDefinitions] = useState(defaultMotorParameters);
  const [dashboards, setDashboards] = useState([]);
  const [sourceGroupWizard, setSourceGroupWizard] = useState({ open: false, group: null });
  const [equipment, setEquipment] = useState(null);
  const [engineModal, setEngineModal] = useState({ open: false, motor: null });

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/equipments/${equipmentId}/source-configs`);
      setData(response.data);
      
      // Expande todas as categorias por padrão
      const expanded = {};
      response.data.categories?.forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Error fetching source configs:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  const fetchSourceGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/equipments/${equipmentId}/source-groups`);
      setSourceSets(response.data.source_sets || []);
      setParameterDefinitions(response.data.parameter_definitions || defaultMotorParameters);
    } catch (error) {
      console.debug('Source groups not loaded:', error.message);
    }
  }, [equipmentId]);

  const fetchDashboards = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboards/equipment/${equipmentId}`);
      setDashboards(response.data.dashboards || []);
    } catch (error) {
      console.debug('Dashboards not loaded:', error.message);
    }
  }, [equipmentId]);

  const fetchEquipment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/equipments/${equipmentId}`);
      setEquipment(response.data);
    } catch (error) {
      console.debug('Equipment not loaded:', error.message);
    }
  }, [equipmentId]);

  useEffect(() => {
    fetchData();
    fetchSourceGroups();
    fetchDashboards();
    fetchEquipment();
  }, [fetchData, fetchSourceGroups, fetchDashboards, fetchEquipment]);

  useEffect(() => {
    const sourceGroupId = searchParams.get('sourceGroupId');
    const sourceSetKey = searchParams.get('sourceSetKey');
    const shouldConfigure = searchParams.get('configure') === '1';
    if ((!sourceGroupId && !sourceSetKey) || !shouldConfigure) return;

    const loadSourceTarget = async () => {
      try {
        if (sourceSetKey) {
          const response = await axios.get(`${API}/equipments/${equipmentId}/source-groups`);
          setSourceSets(response.data.source_sets || []);
          setParameterDefinitions(response.data.parameter_definitions || defaultMotorParameters);
          const sourceSet = (response.data.source_sets || []).find(item => item.source_set_key === sourceSetKey);
          if (!sourceSet) throw new Error('source_set_not_found');
          setSourceGroupWizard({ open: true, group: sourceSet });
        } else {
          const response = await axios.get(`${API}/equipments/${equipmentId}/source-groups/${sourceGroupId}`);
          setParameterDefinitions(response.data.parameter_definitions || defaultMotorParameters);
          setSourceGroupWizard({ open: true, group: response.data.source_set || response.data.source_group });
        }
      } catch (error) {
        toast.error('Fonte detectada não encontrada');
      }
    };
    loadSourceTarget();
  }, [equipmentId, searchParams]);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchData(), fetchSourceGroups(), fetchDashboards(), fetchEquipment()]);
    setRefreshing(false);
    toast.success('Dados atualizados');
  };

  const handleRenameMotor = async (motorId, label) => {
    try {
      await axios.patch(`${API}/equipments/${equipmentId}/motors/${motorId}`, { label });
      toast.success('Motor renomeado');
      // Refaz equipment (motors) e source-configs (display_names cascateados)
      await Promise.all([fetchEquipment(), fetchData()]);
    } catch (error) {
      console.error('Error renaming motor:', error);
      toast.error(error.response?.data?.detail || 'Erro ao renomear motor');
      throw error;
    }
  };

  const openSourceGroupWizard = (group) => {
    setSourceGroupWizard({ open: true, group });
  };

  const closeSourceGroupWizard = () => {
    setSourceGroupWizard({ open: false, group: null });
    if (searchParams.get('sourceGroupId') || searchParams.get('sourceSetKey')) {
      setSearchParams({});
    }
  };

  const handleConfigureSourceGroup = async (groupId, payload) => {
    try {
      const response = await axios.post(`${API}/equipments/${equipmentId}/source-groups/${groupId}/configure`, payload);
      toast.success(`Fonte configurada (${response.data.widgets_added || 0} card(s) adicionados)`);
      await Promise.all([fetchData(), fetchSourceGroups(), fetchDashboards()]);
    } catch (error) {
      console.error('Error configuring source group:', error);
      toast.error(error.response?.data?.detail || 'Erro ao configurar fonte');
      throw error;
    }
  };

  const handleIgnoreSourceGroup = async (groupId) => {
    try {
      await axios.post(`${API}/equipments/${equipmentId}/source-groups/${groupId}/ignore`);
      toast.success('Fonte ignorada');
      closeSourceGroupWizard();
      await fetchSourceGroups();
    } catch (error) {
      console.error('Error ignoring source group:', error);
      toast.error('Erro ao ignorar fonte');
    }
  };
  
  const openConfigModal = async (item) => {
    // Busca unidades disponíveis
    try {
      const response = await axios.get(`${API}/units/available/${item.source.si_unit || 'unknown'}`);
      setAvailableUnits(response.data.available_units || []);
    } catch {
      setAvailableUnits([{ unit: item.config.display_unit || '', formula: 'value', is_default: true }]);
    }
    setConfigModal({ open: true, item });
  };
  
  const openAlertModal = (item) => {
    setAlertModal({ open: true, item });
  };
  
  const handleSaveConfig = async (configId, formData) => {
    try {
      const response = await axios.put(`${API}/source-configs/${configId}`, formData);
      toast.success('Configuração salva');
      
      // Atualiza apenas o item modificado no state local (sem recarregar tudo)
      setData(prevData => {
        if (!prevData) return prevData;
        
        const updatedSources = prevData.sources.map(item => {
          if (item.config.id === configId) {
            return {
              ...item,
              config: {
                ...item.config,
                ...formData,
                status: 'configured',
              },
              display: {
                ...item.display,
                unit: formData.display_unit || item.display.unit,
              }
            };
          }
          return item;
        });
        
        // Recalcula by_category
        const byCategory = {};
        updatedSources.forEach(item => {
          const category = item.source.category || 'other';
          if (!byCategory[category]) byCategory[category] = [];
          byCategory[category].push(item);
        });
        
        // Recalcula contadores
        const configuredCount = updatedSources.filter(s => s.config.status === 'configured').length;
        const withAlertsCount = updatedSources.filter(s => s.config.alert_enabled).length;
        
        return {
          ...prevData,
          sources: updatedSources,
          by_category: byCategory,
          configured_count: configuredCount,
          with_alerts_count: withAlertsCount,
        };
      });
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Erro ao salvar configuração');
      throw error;
    }
  };
  
  const handleSaveAlerts = async (configId, alertEnabled, zones) => {
    try {
      await axios.put(`${API}/source-configs/${configId}`, {
        alert_enabled: alertEnabled,
        alert_zones: zones,
      });
      toast.success('Alertas configurados');
      
      // Atualiza apenas o item modificado no state local (sem recarregar tudo)
      setData(prevData => {
        if (!prevData) return prevData;
        
        const updatedSources = prevData.sources.map(item => {
          if (item.config.id === configId) {
            return {
              ...item,
              config: {
                ...item.config,
                alert_enabled: alertEnabled,
                alert_zones: zones,
              }
            };
          }
          return item;
        });
        
        // Recalcula by_category
        const byCategory = {};
        updatedSources.forEach(item => {
          const category = item.source.category || 'other';
          if (!byCategory[category]) byCategory[category] = [];
          byCategory[category].push(item);
        });
        
        // Recalcula contadores
        const configuredCount = updatedSources.filter(s => s.config.status === 'configured').length;
        const withAlertsCount = updatedSources.filter(s => s.config.alert_enabled).length;
        
        return {
          ...prevData,
          sources: updatedSources,
          by_category: byCategory,
          configured_count: configuredCount,
          with_alerts_count: withAlertsCount,
        };
      });
    } catch (error) {
      console.error('Error saving alerts:', error);
      toast.error('Erro ao salvar alertas');
      throw error;
    }
  };
  
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Nenhum dado encontrado</p>
      </div>
    );
  }

  const pendingSourceSets = sourceSets.filter(sourceSet => sourceSet.status === 'pending');
  
  return (
    <div className="page-container space-y-6" data-testid="source-registry-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/equipments')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{data.equipment_name}</h1>
            <p className="text-muted-foreground">
              {data.equipment_tag} • {data.total_sources} sources
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge 
            variant="outline" 
            className={data.equipment_status === 'online' ? 'bg-green-500/10 text-green-500' : 'bg-muted text-muted-foreground'}
          >
            {data.equipment_status === 'online' ? 'Online' : 'Offline'}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-muted/50 border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{data.total_sources}</div>
            <div className="text-sm text-muted-foreground">Sources Descobertos</div>
          </CardContent>
        </Card>
        <Card className="bg-muted/50 border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-500">{data.configured_count}</div>
            <div className="text-sm text-muted-foreground">Configurados</div>
          </CardContent>
        </Card>
        <Card className="bg-muted/50 border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-500">{data.with_alerts_count}</div>
            <div className="text-sm text-muted-foreground">Com Alertas</div>
          </CardContent>
        </Card>
      </div>

      {/* Motores — renomear designação (ENGINE1 → DRAGA) */}
      {equipment?.motors?.length > 0 && (
        <Card className="bg-muted/30 border-border" data-testid="motors-section">
          <CardHeader className="py-3">
            <div className="flex items-center gap-3">
              <Gauge className="w-5 h-5 text-orange-500" />
              <CardTitle className="text-base">Motores</CardTitle>
              <Badge variant="secondary" className="ml-2">{equipment.motors.length}</Badge>
            </div>
            <CardDescription>
              Renomeie a designação de cada motor (ex.: ENGINE1 → DRAGA). O nome é
              aplicado aos parâmetros, ao dashboard e ao histórico.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <div className="space-y-2">
              {equipment.motors.map((motor) => (
                <div
                  key={motor.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
                  data-testid={`motor-item-${motor.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{motor.label || motor.id}</div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono truncate">{motor.id}</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setEngineModal({ open: true, motor })}
                    title="Renomear motor"
                    data-testid={`rename-motor-btn-${motor.id}`}
                  >
                    <Settings2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {pendingSourceSets.length > 0 && (
        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              <div>
                <h2 className="font-semibold">Novas fontes detectadas</h2>
                <p className="text-sm text-muted-foreground">
                  Configure ou ignore as fontes para remover o alerta persistente.
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30">
              {pendingSourceSets.length}
            </Badge>
          </div>
          <div className="mt-3 space-y-2">
            {pendingSourceSets.map(sourceSet => (
              <div key={sourceSet.source_set_key} className="flex items-center justify-between gap-3 rounded-md bg-card/40 p-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{sourceSet.source_set_label}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{sourceSet.candidate_count} instância(s) candidata(s)</span>
                    <Badge variant="outline" className={sourceStatusClasses.live}>
                      {sourceSet.live_count || 0} recebendo
                    </Badge>
                    <Badge variant="outline" className={sourceStatusClasses.static}>
                      {sourceSet.static_count || 0} estática(s)
                    </Badge>
                    <Badge variant="outline" className={sourceStatusClasses.stale}>
                      {sourceSet.stale_count || 0} sem atualização
                    </Badge>
                  </div>
                </div>
                <Button size="sm" onClick={() => openSourceGroupWizard(sourceSet)} data-testid={`configure-source-set-${sourceSet.source_set_key}`}>
                  <Settings2 className="w-4 h-4 mr-2" />
                  Configurar
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Sources by Category */}
      <div className="space-y-3">
        {data.categories?.map((category) => {
          const CategoryIcon = categoryIcons[category] || Activity;
          const sources = data.by_category[category] || [];
          const isExpanded = expandedCategories[category];
          
          return (
            <Collapsible
              key={category}
              open={isExpanded}
              onOpenChange={() => toggleCategory(category)}
            >
              <Card className="bg-muted/30 border-border">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        )}
                        <CategoryIcon className={`w-5 h-5 ${categoryColors[category]}`} />
                        <CardTitle className="text-base capitalize">
                          {category}
                        </CardTitle>
                        <Badge variant="secondary" className="ml-2">
                          {sources.length}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <CardContent className="pt-0 pb-3">
                    <div className="space-y-2">
                      {sources.map((item) => (
                        <SourceItem
                          key={item.source.id}
                          item={item}
                          onConfigure={openConfigModal}
                          onToggleAlert={openAlertModal}
                        />
                      ))}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          );
        })}
      </div>
      
      {/* Modals */}
      <ConfigModal
        isOpen={configModal.open}
        onClose={() => setConfigModal({ open: false, item: null })}
        item={configModal.item}
        availableUnits={availableUnits}
        onSave={handleSaveConfig}
      />
      
      <AlertModal
        isOpen={alertModal.open}
        onClose={() => setAlertModal({ open: false, item: null })}
        item={alertModal.item}
        onSave={handleSaveAlerts}
      />

      <SourceGroupWizard
        open={sourceGroupWizard.open}
        onClose={closeSourceGroupWizard}
        group={sourceGroupWizard.group}
        parameterDefinitions={parameterDefinitions}
        dashboards={dashboards}
        onConfigure={handleConfigureSourceGroup}
        onIgnore={handleIgnoreSourceGroup}
      />

      <EngineLabelModal
        isOpen={engineModal.open}
        onClose={() => setEngineModal({ open: false, motor: null })}
        motor={engineModal.motor}
        onSave={handleRenameMotor}
      />
    </div>
  );
};

export default SourceRegistryPage;
